import { useLazyQuery } from '@apollo/client';
import { Image } from 'antd';
import { isArray, map } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import {
  BREAKPOINTS,
  DATE_ISO_FORMAT,
  DEFAULT_PAGE_SIZE,
  DEFAULTDATEFORMAT,
  FALLBACK_PDF_URL,
  ROUTES,
  TAB_KEYS,
} from '../../../../../../../common/constants';
import useRouter from '../../../../../../../common/useRouter';
import { titleCase, toCamelCase } from '../../../../../../../common/utils';
import CommonCard from '../../../../../../../components/CommonCard';
import CommonTable from '../../../../../../../components/CommonTable';
import InfiniteScrollHandler from '../../../../../../../components/InfiniteScrollHandler';
import { GET_REGISTER_ENTRIES } from '../../../../../graphql/Queries';

const EntriesTable = ({
  registerDetailsData,
  registerId,
  showListView,
  setIsDataEmpty,
  toDate,
  fromDate,
}) => {
  const {
    navigate,
    location,
    params: { projectId },
  } = useRouter();
  const [entriesData, setEntriesData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const initialRegisterEntryFilter = { skip: 0, limit: DEFAULT_PAGE_SIZE };
  const initialPaginationValue = {
    total: 0,
    current: 1,
  };
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [registerEntryFilter, setRegisterEntryFilter] = useState(
    initialRegisterEntryFilter,
  );
  const registerNavFilter = location?.state?.registerNavFilter;

  const [getRegisterEntries, { loading }] = useLazyQuery(GET_REGISTER_ENTRIES, {
    fetchPolicy: 'cache-and-network',
    onError() {},
    onCompleted(res) {
      const data = res?.registerLogEntryByRegisterList?.data;
      const pagination = {
        ...paginationProp,
        total: res?.registerLogEntryByRegisterList?.total,
      };
      const result = map(data, (item, index) => {
        const obj = Object.keys(item)?.reduce((acc, curr) => {
          const newKey =
            curr === 'createdAt' ||
            curr === 'createdBy' ||
            curr === 'registerLogId'
              ? curr
              : toCamelCase(curr);
          if (curr === 'createdAt' && item[curr]) {
            acc[newKey] = moment(item[curr])?.format(DEFAULTDATEFORMAT);
          } else {
            acc[newKey] = item[curr];
          }
          return acc;
        }, {});
        obj.index = registerEntryFilter?.skip + index + 1;
        return obj;
      });
      setIsDataEmpty(!(res?.registerLogEntryByRegisterList?.total > 0));

      if (scrollFlag) {
        setEntriesData([...entriesData, ...result]);
        setScrollFlag(false);
      } else {
        setEntriesData(result);
      }
      setHasMore(!!data?.length);
      setPaginationProp(pagination);
    },
  });

  useEffect(() => {
    getRegisterEntries({
      variables: { registerId, filter: registerEntryFilter },
    });
  }, [registerEntryFilter]);

  useEffect(() => {
    if (fromDate && toDate) {
      setRegisterEntryFilter({
        ...registerEntryFilter,
        fromDate,
        toDate,
      });
    } else {
      setRegisterEntryFilter(initialRegisterEntryFilter);
    }
  }, [fromDate, toDate]);

  const handleTableChange = (pagination) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setPaginationProp({ ...paginationProp, ...pagination });
    setRegisterEntryFilter({
      ...registerEntryFilter,
      skip,
      limit: pagination.pageSize,
    });
    getRegisterEntries({
      variables: {
        registerId,
        filter: {
          ...registerEntryFilter,
          skip,
          limit: pagination.pageSize,
        },
      },
    });
  };

  const handleRefetch = () => {
    getRegisterEntries({
      variables: {
        registerId,
        filter: {
          ...registerEntryFilter,
          skip: entriesData?.length,
          limit: DEFAULT_PAGE_SIZE,
        },
      },
    });
  };

  const handleRowClick = (record) => {
    const registerLogId = record?.registerLogId;
    navigate(
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/${registerLogId}`,
      {
        state: {
          registerDetailsData,
          currentPath: location?.pathname,
          showListView,
          registerNavFilter,
          registerId,
        },
      },
    );
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      render: (text, record, index) => (
        <div>{registerEntryFilter?.skip + index + 1}</div>
      ),
    },
    ...map(registerDetailsData?.registerDetails, (data) => ({
      title: data?.isRequired ? (
        <div className="d-flex">
          <div className="danger-text mr-5">*</div>
          <div className="mr-5">{data?.name}</div>
        </div>
      ) : (
        `${data?.name}`
      ),
      isRequired: `${data?.isRequired}`,
      dataIndex: `${toCamelCase(data?.name)}`,
      index: `${toCamelCase(data?.name)}`,
      render: (text) => {
        if (moment(text, DATE_ISO_FORMAT, true).isValid()) {
          return moment(text)?.format(DEFAULTDATEFORMAT);
        }
        return <div>{text || '-'}</div>;
      },
    })),
    ...map(registerDetailsData?.registerFormFields, (data) => ({
      title: data?.isRequired ? (
        <div className="d-flex">
          <div className="danger-text mr-5">*</div>
          <div className="mr-5">{data?.name}</div>
        </div>
      ) : (
        `${data?.name}`
      ),
      isRequired: `${data?.isRequired}`,
      dataIndex: `${toCamelCase(data?.name)}`,
      index: `${toCamelCase(data?.name)}`,
      render: (text) => {
        if (moment(text, DATE_ISO_FORMAT, true)?.isValid()) {
          return moment(text)?.format(DEFAULTDATEFORMAT);
        }
        if (isArray(text)) {
          return (
            <div className="d-flex align-center">
              <Image
                wrapperClassName="mr-5 mb-5"
                height={50}
                width={50}
                src={text[0]?.value}
                fallback={FALLBACK_PDF_URL}
              />
              {text?.length - 1 > 0 && (
                <span className="ml-5 pointer extra-count">
                  +{text?.length - 1}
                </span>
              )}
            </div>
          );
        }
        return text || '-';
      },
    })),
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      index: 'createdAt',
    },
  ];

  return (
    <div className="pointer">
      {isDesktopViewport ? (
        <CommonTable
          data={entriesData}
          columns={columns}
          loading={loading}
          onChange={handleTableChange}
          paginationConfig={paginationProp}
          onRow={(record) => {
            return {
              onClick: () => handleRowClick(record),
            };
          }}
          scroll={{ x: true }}
        />
      ) : (
        <InfiniteScrollHandler
          scrollFlag={scrollFlag}
          refetchData={handleRefetch}
          setScrollFlag={setScrollFlag}
          hasMore={hasMore}
          loading={loading}
          dataLength={entriesData?.length}
        >
          <div>
            {map(entriesData, (data, index) => {
              return (
                <CommonCard key={index} onClick={() => handleRowClick(data)}>
                  <div className="common-card d-flex">
                    <div className="mr-5 fw-medium">{index + 1}.</div>
                    <div>
                      <div className="card-content text-secondary">
                        <br />
                        {Object.entries(data)
                          ?.filter(
                            ([key]) =>
                              key !== 'registerLogId' && key !== 'index',
                          )
                          ?.map(([key, value]) => {
                            const fieldDetail =
                              registerDetailsData?.registerFormFields?.find(
                                (detail) => toCamelCase(detail?.name) === key,
                              ) ||
                              registerDetailsData?.registerDetails?.find(
                                (detail) => toCamelCase(detail?.name) === key,
                              );
                            return (
                              <div className="mb-15" key={key}>
                                {fieldDetail?.isRequired && (
                                  <span className="danger-text mr-5">*</span>
                                )}
                                <span className="fw-medium mr-5">
                                  {key === 'createdAt'
                                    ? 'Created On'
                                    : titleCase(key)}
                                  :
                                </span>
                                <span>
                                  {
                                    // eslint-disable-next-line no-nested-ternary
                                    Array?.isArray(value)
                                      ? value?.map((imgUrl) => {
                                          return (
                                            <Image
                                              key={imgUrl?.value}
                                              wrapperClassName="mr-5 mb-5"
                                              height={50}
                                              width={50}
                                              src={imgUrl}
                                              fallback={FALLBACK_PDF_URL}
                                            />
                                          );
                                        })
                                      : moment(
                                            value,
                                            DATE_ISO_FORMAT,
                                            true,
                                          )?.isValid()
                                        ? moment(value)?.format(
                                            DEFAULTDATEFORMAT,
                                          )
                                        : value || '-'
                                  }
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </CommonCard>
              );
            })}
          </div>
        </InfiniteScrollHandler>
      )}
    </div>
  );
};

export default EntriesTable;
